import {
  type DocumentNode,
  type MutationHookOptions,
  type OperationVariables,
  type QueryHookOptions,
  useMutation,
  useQuery,
} from "@apollo/client";
import {
  AudienceEstimateQuery,
  AutomationQuery,
  AutomationsListQuery,
} from "../graphql/queries";
import {
  ArchiveAutomationsMutation,
  CancelBroadcastMutation,
  CreateBroadcastMutation,
  DeleteAutomationsMutation,
  SendBroadcastMutation,
  UpdateBroadcastMutation,
} from "../graphql/mutations";
import type {
  ArchiveAutomationsMutationType,
  ArchiveAutomationsMutationVariablesType,
  AudienceEstimateQueryType,
  AudienceEstimateQueryVariablesType,
  AutomationQueryType,
  AutomationQueryVariablesType,
  AutomationsListQueryType,
  AutomationsListQueryVariablesType,
  CancelBroadcastMutationType,
  CancelBroadcastMutationVariablesType,
  CreateBroadcastMutationType,
  CreateBroadcastMutationVariablesType,
  DeleteAutomationsMutationType,
  DeleteAutomationsMutationVariablesType,
  SendBroadcastMutationType,
  SendBroadcastMutationVariablesType,
  UpdateBroadcastMutationType,
  UpdateBroadcastMutationVariablesType,
} from "@/types/graphql/partner.generated";

export const useAutomationsListQuery = (
  options?: QueryHookOptions<
    AutomationsListQueryType,
    AutomationsListQueryVariablesType
  >
) =>
  useQuery<AutomationsListQueryType, AutomationsListQueryVariablesType>(
    AutomationsListQuery,
    options
  );

export const useAutomationQuery = (
  options?: QueryHookOptions<AutomationQueryType, AutomationQueryVariablesType>
) =>
  useQuery<AutomationQueryType, AutomationQueryVariablesType>(
    AutomationQuery,
    options
  );

// Оценка аудитории пересчитывается на каждое изменение условий в форме,
// вызывающий сам управляет `skip`/`variables` (по образцу
// useEmailTemplatePreviewQuery).
export const useAudienceEstimateQuery = (
  options?: QueryHookOptions<
    AudienceEstimateQueryType,
    AudienceEstimateQueryVariablesType
  >
) =>
  useQuery<AudienceEstimateQueryType, AudienceEstimateQueryVariablesType>(
    AudienceEstimateQuery,
    options
  );

// Список и карточка рассылки перечитываются после каждой мутации (по
// образцу useEmailTemplateMutations): состав, статус и статистика меняются,
// а кеш по id не обновит список сам. Архив и удаление общие для рассылок и
// автоматизаций по событию (фаза 3, Task 17), поэтому перечитываем оба
// списка и обе карточки: одна и та же мутация может задеть строку любого
// вида автоматизации.
const REFETCH = {
  refetchQueries: [
    "AutomationsList",
    "Automation",
    "AutomationFlowsList",
    "AutomationEditor",
  ],
};

const useRefetchingMutation = <TData, TVariables extends OperationVariables>(
  mutation: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
) => useMutation<TData, TVariables>(mutation, { ...REFETCH, ...options });

export const useCreateBroadcastMutation = (
  options?: MutationHookOptions<
    CreateBroadcastMutationType,
    CreateBroadcastMutationVariablesType
  >
) => useRefetchingMutation(CreateBroadcastMutation, options);

export const useUpdateBroadcastMutation = (
  options?: MutationHookOptions<
    UpdateBroadcastMutationType,
    UpdateBroadcastMutationVariablesType
  >
) => useRefetchingMutation(UpdateBroadcastMutation, options);

export const useSendBroadcastMutation = (
  options?: MutationHookOptions<
    SendBroadcastMutationType,
    SendBroadcastMutationVariablesType
  >
) => useRefetchingMutation(SendBroadcastMutation, options);

export const useCancelBroadcastMutation = (
  options?: MutationHookOptions<
    CancelBroadcastMutationType,
    CancelBroadcastMutationVariablesType
  >
) => useRefetchingMutation(CancelBroadcastMutation, options);

export const useArchiveAutomationsMutation = (
  options?: MutationHookOptions<
    ArchiveAutomationsMutationType,
    ArchiveAutomationsMutationVariablesType
  >
) => useRefetchingMutation(ArchiveAutomationsMutation, options);

export const useDeleteAutomationsMutation = (
  options?: MutationHookOptions<
    DeleteAutomationsMutationType,
    DeleteAutomationsMutationVariablesType
  >
) => useRefetchingMutation(DeleteAutomationsMutation, options);
